import React, { useState, useRef, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ArrowLeft, Search, X, MoreHorizontal } from 'lucide-react';
import Pagination from '../../components/Pagination';
import noImage from '../../assets/img/brand/noImage.png';
import noBrandImage from '../../assets/img/brand/no-brand-img.png';
import emptySearchImg from '../../assets/img/brand/empty-search.png';
import TradeInItemDrawer from '../../components/TradeInItemDrawer';
import { getDeviceDefaultImage } from './tradeInImages.js';
import FilterDrawer from '../../components/FilterDrawer';
import apiHelper from '../../apiHelper';

const PAGE_SIZE = 20;
const TIERS_PER_ITEM = 4;
// Item + Condition + BasePrice + Activated
// + Receipt(3) + SetType(2) + Screen(2) + Exterior(2)
// + Battery(2) + Accessories(2) + Issues(4) + Action
const TOTAL_COLS = 22;

// ─── Tier definitions ─────────────────────────────────────────────────────────
// Each of the 4 rows shows ONE sub-item per group, mapped by tier index.
const makeTiers = (firstLabel) => [
    {
        label: firstLabel,
        receiptValue:    0,
        receiptLabel:    null,
        receiptWhatsapp: null,
        setTypeLabel:    null,
        setTypeValue:    null,
        screenLabel:     'Flawless',
        screenValue:     'N/A',
        exteriorLabel:   'Flawless',
        exteriorValue:   'N/A',
        batteryLabel:    '>90%',
        batteryValue:    'N/A',
        accessoryLabel:  'Box',
        accessoryValue:  'N/A',
        issue1Label:     'Unable to power on',
        issue1Value:     'N/A',
        issue2Label:     'Touch Screen Faulty',
        issue2Value:     'N/A',
    },
    {
        label: 'Within 3 Days',
        receiptValue:    -100,
        receiptLabel:    'With',
        receiptWhatsapp: 'N/A',
        setTypeLabel:    'Local',
        setTypeValue:    0,
        screenLabel:     'Minor Scratches',
        screenValue:     'N/A',
        exteriorLabel:   'Minor Scratches',
        exteriorValue:   'N/A',
        batteryLabel:    '86-89%',
        batteryValue:    'N/A',
        accessoryLabel:  'Charging Cable',
        accessoryValue:  'N/A',
        issue1Label:     'Fingerprint / Face id',
        issue1Value:     'N/A',
        issue2Label:     'Lock Hours',
        issue2Value:     'N/A',
    },
    {
        label: 'Within 7 Days',
        receiptValue:    -120,
        receiptLabel:    'Without',
        receiptWhatsapp: 'N/A',
        setTypeLabel:    'Export',
        setTypeValue:    'WhatsApp',
        screenLabel:     'Moderate Scratches',
        screenValue:     'N/A',
        exteriorLabel:   'Moderate Scratches',
        exteriorValue:   'N/A',
        batteryLabel:    '81-85%',
        batteryValue:    'N/A',
        accessoryLabel:  'Earphone',
        accessoryValue:  'N/A',
        issue1Label:     'Display Blemish / Defect',
        issue1Value:     'N/A',
        issue2Label:     'Disable Doorstep',
        issue2Value:     0,
    },
    {
        label: 'Within 14 Days',
        receiptValue:    -150,
        receiptLabel:    null,
        receiptWhatsapp: null,
        setTypeLabel:    null,
        setTypeValue:    null,
        screenLabel:     'Cracked or Chipped',
        screenValue:     'N/A',
        exteriorLabel:   'Cracked or Chipped',
        exteriorValue:   'N/A',
        batteryLabel:    'Battery Service',
        batteryValue:    'N/A',
        accessoryLabel:  'Charger',
        accessoryValue:  'N/A',
        issue1Label:     null,
        issue1Value:     null,
        issue2Label:     null,
        issue2Value:     null,
    },
];


// ─── Sub-components ───────────────────────────────────────────────────────────

const PromoIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ flexShrink: 0 }}>
        <g clipPath="url(#clip0_9446_29093)">
            <path fillRule="evenodd" clipRule="evenodd" d="M5 9.375C6.16032 9.375 7.27312 8.91406 8.09359 8.09359C8.91406 7.27312 9.375 6.16032 9.375 5C9.375 3.83968 8.91406 2.72688 8.09359 1.90641C7.27312 1.08594 6.16032 0.625 5 0.625C3.83968 0.625 2.72688 1.08594 1.90641 1.90641C1.08594 2.72688 0.625 3.83968 0.625 5C0.625 6.16032 1.08594 7.27312 1.90641 8.09359C2.72688 8.91406 3.83968 9.375 5 9.375ZM5 10C6.32608 10 7.59785 9.47322 8.53553 8.53553C9.47322 7.59785 10 6.32608 10 5C10 3.67392 9.47322 2.40215 8.53553 1.46447C7.59785 0.526784 6.32608 0 5 0C3.67392 0 2.40215 0.526784 1.46447 1.46447C0.526784 2.40215 0 3.67392 0 5C0 6.32608 0.526784 7.59785 1.46447 8.53553C2.40215 9.47322 3.67392 10 5 10Z" fill="#5E6E82"/>
            <path d="M5.58122 4.11743L4.14997 4.29681L4.09872 4.53431L4.37997 4.58618C4.56372 4.62993 4.59997 4.69618 4.55997 4.87931L4.09872 7.04681C3.97747 7.60743 4.16435 7.87118 4.60372 7.87118C4.94435 7.87118 5.33997 7.71368 5.51935 7.49743L5.57434 7.23743C5.44934 7.34743 5.26684 7.39118 5.1456 7.39118C4.97372 7.39118 4.91122 7.27056 4.9556 7.05806L5.58122 4.11743Z" fill="#5E6E82"/>
            <path d="M5 3.4375C5.34518 3.4375 5.625 3.15768 5.625 2.8125C5.625 2.46732 5.34518 2.1875 5 2.1875C4.65482 2.1875 4.375 2.46732 4.375 2.8125C4.375 3.15768 4.65482 3.4375 5 3.4375Z" fill="#5E6E82"/>
        </g>
        <defs>
            <clipPath id="clip0_9446_29093">
                <rect width="10" height="10" fill="white"/>
            </clipPath>
        </defs>
    </svg>
);

const TypeBadge = ({ isPromotion }) => {
    const s = isPromotion
        ? { bg: 'var(--Utilised-blue-1, #CFE2FF)', color: 'var(--Utilised-blue-2, #2C7BE5)', border: 'var(--Utilised-blue-2, #2C7BE5)' }
        : { bg: 'var(--Utilised-background-2, #F8F9FA)', color: 'var(--Utilised-text-2, #6C757D)', border: 'var(--Utilised-text-2, #6C757D)' };
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            padding: '2px 5px', borderRadius: '99px', gap: '10px',
            fontSize: '8px', fontWeight: 600, fontStyle: 'normal', lineHeight: 'normal',
            fontFamily: 'Montserrat', textAlign: 'center',
            background: s.bg, color: s.color,
            border: `1px solid ${s.border}`, whiteSpace: 'nowrap',
        }}>
            {isPromotion ? 'Promotional' : 'Normal'}
        </span>
    );
};

const ConditionBadge = ({ condition }) => (
    <span style={{
        borderRadius: '99px',
        border: '1px solid var(--Primary-Color, #FFEB00)',
        background: 'var(--Primary-Color, #FFEB00)',
        display: 'flex', height: '20px', minWidth: '72px',
        padding: '5px 10px', justifyContent: 'center', alignItems: 'center',
        gap: '5px', overflow: 'hidden',
        color: 'var(--navbar-dropdown-active, #212529)',
        textAlign: 'center', textOverflow: 'ellipsis',
        fontFamily: 'Montserrat', fontSize: '12px', fontStyle: 'normal',
        fontWeight: 600, lineHeight: 'normal',
        whiteSpace: 'nowrap',
    }}>
        {condition}
    </span>
);

// Format a value: numbers get 2 decimal places + thousand separator; strings pass through.
const fmtVal = (v) => {
    if (v === null || v === undefined || v === '') return v;
    // Real JS number (e.g. 0, -100)
    if (typeof v === 'number' && !isNaN(v)) {
        return v.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    // String that is purely numeric, e.g. "-100.00", "0.00", "1234.5"
    if (typeof v === 'string') {
        const trimmed = v.trim();
        // Matches optional minus, digits, optional decimal part — no letters
        if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
            const parsed = parseFloat(trimmed);
            return parsed.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        }
    }
    return v;
};

const Val = ({ v }) => {
    if (v === null || v === undefined || v === '') return null;
    return (
        <span style={{
            fontFamily: 'Montserrat', fontSize: '12px', fontStyle: 'normal',
            fontWeight: 400, lineHeight: 'normal',
            color: 'var(--Gray-900, #212529)',
        }}>
            {fmtVal(v)}
        </span>
    );
};

const Lbl = ({ v }) => {
    if (!v) return null;
    return (
        <span style={{
            fontFamily: 'Montserrat', fontSize: '11px', fontStyle: 'normal',
            fontWeight: 400, lineHeight: 'normal',
            color: 'var(--Gray-600, #6C757D)', whiteSpace: 'nowrap',
        }}>
            {v}
        </span>
    );
};

const PromoPopup = ({ promo }) => (
    <div style={{
        position: 'absolute', bottom: 'calc(100% + 8px)', left: '0%',
        borderRadius: '8px',
        border: '1px solid var(--Background-Gray, #E9ECEF)',
        background: 'var(--Gray-100, #F8F9FA)',
        display: 'inline-flex', flexDirection: 'column',
        padding: '10px', justifyContent: 'center', alignItems: 'flex-start',
        gap: '6px',
        zIndex: 99999, pointerEvents: 'none',
        minWidth: '355px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
    }}>
        <div style={{
            fontFamily: 'Montserrat', fontSize: '12px', fontStyle: 'normal',
            fontWeight: 700, lineHeight: 'normal',
            color: 'var(--Gray-900, #212529)',
        }}>
            Promotional Info
        </div>
        <div style={{
            fontFamily: 'Montserrat', fontSize: '12px', fontStyle: 'normal',
            fontWeight: 400, lineHeight: 'normal',
            color: 'var(--Gray-900, #212529)',
        }}>
            <span >Campaign Name: </span>{promo?.campaignName}
        </div>
        <div style={{
            fontFamily: 'Montserrat', fontSize: '12px', fontStyle: 'normal',
            fontWeight: 400, lineHeight: 'normal',
            color: 'var(--Gray-900, #212529)',
        }}>
            <span >Promotional Period: </span>{promo?.period}
        </div>
        <div style={{
            position: 'absolute', bottom: '-5px', left: '50%', transform: 'translateX(-50%)',
            width: 0, height: 0,
            borderLeft: '5px solid transparent', borderRight: '5px solid transparent',
            borderTop: '5px solid #E9ECEF',
        }} />
    </div>
);

// ─── Build tier rows from live API deductions ─────────────────────────────────
// PRD fixed positions (0-indexed): Activation 0-3, Receipt 4-5, SetType 6-7,
// Screen 8-11, Exterior 12-15, Battery 16-19, Accessories 20-23, Issues 24-26 + 27-29
function makeApiTiers(deductions) {
    const d = (i) => deductions[i] || null;
    // Name-based lookup — immune to sort_order gaps or missing deductions
    const byName = (name) => deductions.find(x => x.deduction_item_name === name) || null;

    // ── DEBUG: log deductions to check names ──────────────────────────────────
    console.log('[makeApiTiers] deduction names:', deductions.map(x => `"${x.deduction_item_name}"`));
    console.log('[makeApiTiers] Local Set:', byName('Local Set'));
    console.log('[makeApiTiers] Export Set:', byName('Export Set'));

    const getVal = (deduction) => {
        if (!deduction) return null;
        const t = deduction.deduction_value_type;
        if (t === 'N/A')      return 'N/A';
        if (t === 'WhatsApp') return 'WhatsApp';
        if (t === 'Formula')  return deduction.deduction_formula_result ?? deduction.deduction_locked_value_text ?? null;
        if (t === 'Numeric')  return deduction.deduction_numeric_value ?? null;
        return deduction.deduction_locked_value_text ?? null;
    };
    const getName = (deduction) => deduction?.deduction_item_name || null;

    // Receipt label: strip " Official Receipt" → show only "With" or "Without"
    const getReceiptLabel = (deduction) => {
        const name = deduction?.deduction_item_name || null;
        if (!name) return null;
        const lower = name.toLowerCase();
        if (lower.includes('without')) return 'Without';
        if (lower.includes('with'))    return 'With';
        return name;
    };

    // Set Type label: strip " set" / " Set" → show only "Local" or "Export"
    const getSetTypeLabel = (deduction) => {
        const name = deduction?.deduction_item_name || null;
        if (!name) return null;
        const lower = name.toLowerCase();
        if (lower.includes('local'))  return 'Local';
        if (lower.includes('export')) return 'Export';
        return name;
    };

    // Set Type value: WhatsApp type → 'WhatsAPP'; Numeric → integer (no ".00")
    const getSetTypeVal = (deduction) => {
        if (!deduction) return null;
        const t = deduction.deduction_value_type;
        if (t === 'WhatsApp') return 'WhatsApp';
        if (t === 'Numeric') {
            const n = parseFloat(deduction.deduction_numeric_value);
            return isNaN(n) ? null : n;
        }
        return getVal(deduction);
    };

    return [
        {
            label:           getName(d(0)),
            receiptValue:    getVal(d(0)),
            receiptLabel:    null,
            receiptWhatsapp: null,
            setTypeLabel:    null,
            setTypeValue:    null,
            screenLabel:     getName(d(8)),
            screenValue:     getVal(d(8)),
            exteriorLabel:   getName(d(12)),
            exteriorValue:   getVal(d(12)),
            batteryLabel:    getName(d(16)),
            batteryValue:    getVal(d(16)),
            accessoryLabel:  getName(d(20)),
            accessoryValue:  getVal(d(20)),
            issue1Label:     getName(d(24)),
            issue1Value:     getVal(d(24)),
            issue2Label:     getName(d(27)),
            issue2Value:     getVal(d(27)),
        },
        {
            label:           getName(d(1)),
            receiptValue:    getVal(d(1)),
            receiptLabel:    getReceiptLabel(d(4)),
            receiptWhatsapp: getVal(d(4)),
            setTypeLabel:    getSetTypeLabel(byName('Local Set')),
            setTypeValue:    getSetTypeVal(byName('Local Set')),
            screenLabel:     getName(d(9)),
            screenValue:     getVal(d(9)),
            exteriorLabel:   getName(d(13)),
            exteriorValue:   getVal(d(13)),
            batteryLabel:    getName(d(17)),
            batteryValue:    getVal(d(17)),
            accessoryLabel:  getName(d(21)),
            accessoryValue:  getVal(d(21)),
            issue1Label:     getName(d(25)),
            issue1Value:     getVal(d(25)),
            issue2Label:     getName(d(28)),
            issue2Value:     getVal(d(28)),
        },
        {
            label:           getName(d(2)),
            receiptValue:    getVal(d(2)),
            receiptLabel:    getReceiptLabel(d(5)),
            receiptWhatsapp: getVal(d(5)),
            setTypeLabel:    getSetTypeLabel(byName('Export Set')),
            setTypeValue:    getSetTypeVal(byName('Export Set')),
            screenLabel:     getName(d(10)),
            screenValue:     getVal(d(10)),
            exteriorLabel:   getName(d(14)),
            exteriorValue:   getVal(d(14)),
            batteryLabel:    getName(d(18)),
            batteryValue:    getVal(d(18)),
            accessoryLabel:  getName(d(22)),
            accessoryValue:  getVal(d(22)),
            issue1Label:     getName(d(26)),
            issue1Value:     getVal(d(26)),
            issue2Label:     getName(d(29)),
            issue2Value:     getVal(d(29)),
        },
        {
            label:           getName(d(3)),
            receiptValue:    getVal(d(3)),
            receiptLabel:    null,
            receiptWhatsapp: null,
            setTypeLabel:    null,
            setTypeValue:    null,
            screenLabel:     getName(d(11)),
            screenValue:     getVal(d(11)),
            exteriorLabel:   getName(d(15)),
            exteriorValue:   getVal(d(15)),
            batteryLabel:    getName(d(19)),
            batteryValue:    getVal(d(19)),
            accessoryLabel:  getName(d(23)),
            accessoryValue:  getVal(d(23)),
            issue1Label:     null,
            issue1Value:     null,
            issue2Label:     null,
            issue2Value:     null,
        },
    ];
}

// ─── Map API item to the shape the table expects ─────────────────────────────
function mapApiItem(apiItem) {
    const deductions     = apiItem.deductions || [];
    const firstTierLabel = apiItem.tradein_condition === 'Used' ? 'WhatsApp' : 'Not Activated';
    const isWhatsAppPrice = apiItem.base_price_mode
        ? apiItem.base_price_mode === 'WhatsApp'
        : apiItem.base_price_text === 'WhatsApp';
    return {
        id:             apiItem.tradein_listing_item_id,
        storage:        apiItem.storage || '',
        sku_name:       apiItem.sku_name || '',
        color:          apiItem.color || '',
        colorCode:      apiItem.color_code ? `#${apiItem.color_code}` : '#cccccc',
        itemCondition:  apiItem.tradein_condition || '',
        item_status:    apiItem.item_status,
        isPromotion:    apiItem.is_promotion || false,
        promo:          apiItem.promo || null,
        basePrice:      isWhatsAppPrice
                            ? 'WhatsApp'
                            : (apiItem.base_price ? `SGD ${parseFloat(apiItem.base_price).toLocaleString('en-SG', { minimumFractionDigits: 2 })}` : 'N/A'),
        basePriceRaw:   (!isWhatsAppPrice && apiItem.base_price != null && apiItem.base_price !== '') ? parseFloat(apiItem.base_price) : null,
        tiers:          deductions.length > 0 ? makeApiTiers(deductions) : makeTiers(firstTierLabel),
        deductions,
        open_id:        apiItem.open_id || '',
        created_by:     apiItem.created_by || '',
        created_time:   apiItem.created_at || '',
        last_modified_by:   apiItem.modified_by || '',
        last_modified_time: apiItem.modified_at || '',
        tradein_image_url:  apiItem.tradein_image_url || null,
        uuid:  apiItem.uuid || null,
    };
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function TradeInListingItems() {
    const history  = useHistory();
    const location = useLocation();
    const listing  = location.state?.listing || { listing_model: 'Unknown', tradein_listing_id: null };

    const [pageData,         setPageData]         = useState([]);
    const [totalCount,       setTotalCount]       = useState(0);
    const [loading,          setLoading]          = useState(true);
    const [searchInput,      setSearchInput]      = useState('');
    const [searchTerm,       setSearchTerm]       = useState('');
    const [currentPage,      setCurrentPage]      = useState(1);
    const [hoveredPromo,     setHoveredPromo]     = useState(null);
    const [dropdownOpenId,   setDropdownOpenId]   = useState(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
    const [appliedFilter,    setAppliedFilter]    = useState(null);
    const [filterPopupOpen,  setFilterPopupOpen]  = useState(false);
    const [tempValues,       setTempValues]       = useState({ status: [], condition: [], base_price_min: '', base_price_max: '' });
    const [selectedItem,     setSelectedItem]     = useState(null);
    const [drawerOpen,       setDrawerOpen]       = useState(false);
    const dropdownRef = useRef(null);
    const buttonRefs  = useRef({});

    const breadcrumbs = ['Listing', 'Trade-In Listing', listing.listing_model];

    // An item left on the device default in Step 2 stores no image of its own — the
    // placeholder is a fixed asset, never snapshotted per row — so it arrives with
    // tradein_image_url: null. Resolve it back from the bundled asset exactly as the listing
    // level does (TradeInListing.js / TradeInListingDrawer.js), which is the same file Step 2
    // showed. A generic no-image icon here made a perfectly normal item look broken.
    const deviceDefaultImage = getDeviceDefaultImage(listing.listing_device);

    // ── Build query params ──
    const buildParams = (page, search, filter) => {
        const p = new URLSearchParams();
        p.set('page', page);
        p.set('page_size', PAGE_SIZE);
        if (search) p.set('search', search);
        if (filter?.status?.length)    filter.status.forEach(v => p.append('status[]', v));
        if (filter?.condition?.length) filter.condition.forEach(v => p.append('condition[]', v));
        if (filter?.base_price_min)   p.set('price_min', filter.base_price_min);
        if (filter?.base_price_max)   p.set('price_max', filter.base_price_max);
        return p.toString();
    };

    // ── Fetch page from API ──
    const fetchPage = async (page, search, filter, isInitial = false) => {
        const listingId = listing.tradein_listing_id;
        if (!listingId) { setLoading(false); return; }
        if (isInitial) { setLoading(true); }
        try {
            const res = await apiHelper(`sales/trade-in-listings/${listingId}/items/?${buildParams(page, search, filter)}`);
            if (res && res.ok) {
                const data = await res.json();
                setPageData((data.results ?? []).map(mapApiItem));
                setTotalCount(data.count ?? 0);
            }
        } catch (err) {
            console.error('Failed to fetch trade-in items:', err);
        } finally {
            if (isInitial) { setLoading(false); }
        }
    };

    // ── Initial load ──
    useEffect(() => {
        fetchPage(1, '', null, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listing.tradein_listing_id]);

    const totalPages = Math.ceil(totalCount / PAGE_SIZE);
    const startCount = totalCount === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
    const endCount   = Math.min(currentPage * PAGE_SIZE, totalCount);

    const toggleDropdown = (id, e) => {
        if (e) e.stopPropagation();
        if (dropdownOpenId === id) { setDropdownOpenId(null); return; }
        const btn = buttonRefs.current[id];
        if (btn) {
            const r = btn.getBoundingClientRect();
            setDropdownPosition({ top: r.bottom + window.scrollY + 4, right: window.innerWidth - r.right });
        }
        setDropdownOpenId(id);
    };

    useEffect(() => {
        const fn = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target))
                setDropdownOpenId(null);
        };
        if (dropdownOpenId !== null) document.addEventListener('mousedown', fn);
        return () => document.removeEventListener('mousedown', fn);
    }, [dropdownOpenId]);

    useEffect(() => {
        const fn = () => setDropdownOpenId(null);
        window.addEventListener('scroll', fn, true);
        return () => window.removeEventListener('scroll', fn, true);
    }, []);

    const hasActiveFilter = !!(
        appliedFilter?.status?.length ||
        appliedFilter?.condition?.length ||
        appliedFilter?.base_price_min ||
        appliedFilter?.base_price_max
    );
    const isInitialEmpty = !loading && totalCount === 0 && !searchTerm && !hasActiveFilter;
    const hasNoResults   = !loading && pageData.length === 0 && (!!searchTerm || hasActiveFilter);
    const showEmptyState = isInitialEmpty || hasNoResults;
    const emptyImage   = hasNoResults ? emptySearchImg : noBrandImage;
    const emptyTitle   = hasNoResults ? 'No Results Found' : 'No Items Found';
    const emptyMessage = hasNoResults
        ? "No results match what you're looking for"
        : 'Nothing here yet. Add your first item to get started!';

    // ── Back navigation — restore page + drawer state ──
    const handleGoBack = () => {
        history.push('/trade-in-listing', {
            returnPage:     location.state?.returnPage,
            returnDrawerId: location.state?.returnDrawerId,
        });
    };

    const conditionOptions = ['New', 'Used'];

    const filterSections = [
        { label: 'Trade-In Status', type: 'checkbox', key: 'status', options: ["Normal", "Promotional"] },
        { label: 'Condition', type: 'checkbox', key: 'condition', options: conditionOptions },
        { label: 'Base Price', type: 'range', key: 'base_price' },
    ];

    // ── Filter icons (exact match from PlatformOrderPage) ───────────────────
    const FilterActiveIcon = () => (
        <svg
            style={{ margin: 0 }}
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M20.6325 6.44444C20.7252 6.24267 20.7746 6.02858 20.8107 5.74756C21.0354 4.00089 21.1478 3.12756 21.6356 2.56378C20.1234 2 19.2177 2 17.4063 2H4.59369C2.78227 2 1.87655 2 1.36439 2.56378C0.852232 3.12756 0.964572 4.00089 1.18925 5.74756C1.25487 6.25766 1.36399 6.54725 1.69704 6.94296C2.77293 8.22128 4.74334 10.4951 7.50829 12.5623C7.76095 12.7512 7.92676 13.0599 7.95475 13.4016C8.26623 17.2071 8.55159 19.8068 8.70309 21.0865C8.78256 21.7577 9.52673 22.274 10.14 21.8404C11.1702 21.1118 13.0947 20.2952 13.349 18.938C13.4692 18.2964 13.6451 17.1765 13.8437 15.3333"
                stroke="#0D6EFD"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <g clipPath="url(#clip0_4516_4830)">
                <path
                    d="M22.8125 11C22.8125 14.2102 20.2102 16.8125 17 16.8125C13.7898 16.8125 11.1875 14.2102 11.1875 11C11.1875 7.78987 13.7898 5.18753 17 5.18753C20.2102 5.18753 22.8125 7.78987 22.8125 11ZM16.3277 14.0777L20.6402 9.7652C20.7866 9.61877 20.7866 9.38132 20.6402 9.23488L20.1099 8.70456C19.9634 9.5581 19.726 9.5581 19.5795 8.70456L16.0625 12.2215L14.4205 10.5795C14.2741 10.4331 14.0366 10.4331 13.8901 10.5795L13.3598 11.1099C13.2134 11.2563 13.2134 11.4937 13.3598 11.6402L15.7973 14.0777C15.9438 14.2241 16.1812 14.2241 16.3277 14.0777Z"
                    fill="#0D6EFD"
                />
            </g>
            <defs>
                <clipPath id="clip0_4516_4830">
                    <rect width="12" height="12" fill="white" transform="translate(11 5)" />
                </clipPath>
            </defs>
        </svg>
    );

    const FilterInactiveIcon = ({ color = "#6C757D" }) => (
        <svg
            style={{ margin: 0 }}
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
        >
            <path
                d="M21.6323 6.44444C21.7251 6.24267 21.7744 6.02858 21.8106 5.74756C22.0353 4.00089 22.1476 3.12756 21.6354 2.56378C21.1233 2 20.2176 2 18.4062 2H5.59366C3.78225 2 2.87655 2 2.36439 2.56378C1.85223 3.12756 1.96457 4.00089 2.18925 5.74756C2.25487 6.25766 2.36399 6.54725 2.69703 6.94296C3.77291 8.22128 5.74331 10.4951 8.50823 12.5623C8.76089 12.7512 8.9267 13.0599 8.95469 13.4016C9.26616 17.2071 9.55152 19.8068 9.70302 21.0865C9.78249 21.7577 10.5267 22.274 11.1399 21.8404C12.1701 21.1118 14.0946 20.2952 14.3489 18.938C14.4691 18.2964 14.645 17.1765 14.8436 15.3333"
                stroke={color}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M18.1113 7.5556V15.3334M22.0001 11.4445L14.2224 11.4445"
                stroke={color}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );

    const FilterDisabledIcon = () => (
        <svg
            style={{ margin: 0 }}
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
        >
            <path
                d="M21.6323 6.44444C21.7251 6.24267 21.7744 6.02858 21.8106 5.74756C22.0353 4.00089 22.1476 3.12756 21.6354 2.56378C21.1233 2 20.2176 2 18.4062 2H5.59366C3.78225 2 2.87655 2 2.36439 2.56378C1.85223 3.12756 1.96457 4.00089 2.18925 5.74756C2.25487 6.25766 2.36399 6.54725 2.69703 6.94296C3.77291 8.22128 5.74331 10.4951 8.50823 12.5623C8.76089 12.7512 8.9267 13.0599 8.95469 13.4016C9.26616 17.2071 9.55152 19.8068 9.70302 21.0865C9.78249 21.7577 10.5267 22.274 11.1399 21.8404C12.1701 21.1118 14.0946 20.2952 14.3489 18.938C14.4691 18.2964 14.645 17.1765 14.8436 15.3333"
                stroke="#CED4DA"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M18.1113 7.5556V15.3334M22.0001 11.4445L14.2224 11.4445"
                stroke="#CED4DA"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );

    // ── Shared cell styles ──────────────────────────────────────────────────
    const thS = {
        fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 700, fontStyle: 'normal',
        lineHeight: 'normal', color: 'var(--Gray-900, #212529)',
        background: 'var(--Gray-200, #E9ECEF)', height: '45px', padding: '10px',
        whiteSpace: 'nowrap', textAlign: 'center',
    };
    const tdC = {
        fontFamily: 'Montserrat', fontSize: '11px', color: '#212529',
        padding: '5px 8px', verticalAlign: 'middle',
        textAlign: 'center',
    };
    const tdL = { ...tdC, textAlign: 'left' };

    // ─── Render ─────────────────────────────────────────────────────────────
    return (
        <div className="company-body new-look stock-wrap bg-checkbox">

            {/* Breadcrumb */}
            <div className="breadcrumb">
                {breadcrumbs.map((crumb, i) => {
                    const isTradeInListing = crumb === 'Trade-In Listing';
                    return (
                        <span key={i} className="breadcrumb-item">
                            {isTradeInListing ? (
                                <span onClick={handleGoBack} style={{ cursor: 'pointer' }}>
                                    {crumb}
                                </span>
                            ) : (
                                crumb
                            )}
                            {i !== breadcrumbs.length - 1 && <span className="breadcrumb-separator">  </span>}
                        </span>
                    );
                })}
            </div>

            <div className="employee-tabs-container">

                {/* Page header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
                    <button onClick={handleGoBack} style={{
                        background: 'transparent', border: 'none', cursor: 'pointer',
                        padding: '4px', display: 'flex', alignItems: 'center',
                    }}>
                        <ArrowLeft size={20} color="#212529" />
                    </button>
                    <h4 style={{ fontFamily: 'Montserrat', fontSize: '20px', fontWeight: 700, color: '#212529', margin: 0 }}>
                        {listing.listing_model} Items
                    </h4>
                </div>

                <div className="info-section">
                    <div className="info-card" style={{ overflow: 'unset' }}>
                        <div className="entities-tab">

                            {/* Toolbar */}
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                <div style={{ display: "flex", gap: "8px", width: "100%" }}>

                                    {/* Search Box */}
                                    <div className="search-box" style={{ width: "300px", position: 'relative', border: isInitialEmpty ? "1px solid var(--Gray-400, #CED4DA)" : "1px solid var(--Gray-600, #6C757D)", background: isInitialEmpty ? "var(--Gray-100, #F8F9FA)" : "var(--Default-White, #FFF)", pointerEvents: isInitialEmpty ? "none" : "auto" }}>
                                        <Search size={20} color={isInitialEmpty ? "#CED4DA" : "#6C757D"} />
                                        {isInitialEmpty && (
                                            <style>{`.search-box-disabled-til-items::placeholder { color: #CED4DA !important; }`}</style>
                                        )}
                                        <input
                                            type="text"
                                            placeholder="Search..."
                                            disabled={isInitialEmpty}
                                            className={isInitialEmpty ? 'search-box-disabled-til-items' : undefined}
                                            style={{ color: isInitialEmpty ? '#ADB5BD' : undefined }}
                                            value={searchInput}
                                            onChange={(e) => setSearchInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    setSearchTerm(searchInput);
                                                    setCurrentPage(1);
                                                    fetchPage(1, searchInput, appliedFilter);
                                                }
                                            }}
                                        />
                                        {searchInput && (
                                            <button
                                                onClick={() => { setSearchInput(''); setSearchTerm(''); setCurrentPage(1); fetchPage(1, '', appliedFilter); }}
                                                style={{
                                                    position: 'absolute', right: '4px',
                                                    background: 'transparent', border: 'none',
                                                    cursor: 'pointer', display: 'flex',
                                                    alignItems: 'center', justifyContent: 'center', padding: '4px',
                                                }}
                                            >
                                                <X size={16} />
                                            </button>
                                        )}
                                    </div>

                                    {/* Filter Button */}
                                    <button
                                        className={`icon-btn p-2 filter-btn ${appliedFilter ? "applied" : ""}`}
                                        onClick={() => !isInitialEmpty && setFilterPopupOpen((prev) => !prev)}
                                        disabled={isInitialEmpty}
                                        style={{
                                            border: isInitialEmpty
                                                ? "1px solid var(--Gray-400, #CED4DA)"
                                                : appliedFilter
                                                    ? "1px solid #2C7BE5"
                                                    : "1px solid var(--Gray-600, #6C757D)",
                                            color: isInitialEmpty ? "#CED4DA" : appliedFilter ? "#2C7BE5" : "#6C757D",
                                            background: isInitialEmpty
                                                ? "var(--Gray-100, #F8F9FA)"
                                                : appliedFilter
                                                    ? "#CFE2FF"
                                                    : "var(--Default-White, #FFF)",
                                            height: "40px",
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                            pointerEvents: isInitialEmpty ? "none" : "auto",
                                            cursor: isInitialEmpty ? "not-allowed" : "pointer",
                                            borderRadius: "8px",
                                        }}
                                    >
                                        {isInitialEmpty ? (
                                            <FilterDisabledIcon />
                                        ) : appliedFilter ? (
                                            <FilterActiveIcon />
                                        ) : (
                                            <FilterInactiveIcon />
                                        )}
                                    </button>

                                    <FilterDrawer
                                        isOpen={filterPopupOpen}
                                        onClose={() => {
                                            setTempValues({ status: [], condition: [], base_price_min: '', base_price_max: '' });
                                            setFilterPopupOpen(false);
                                        }}
                                        sections={filterSections}
                                        tempValues={tempValues}
                                        setTempValues={setTempValues}
                                        onApply={() => {
                                            const hasAny = tempValues.status?.length || tempValues.condition?.length || tempValues.base_price_min || tempValues.base_price_max;
                                            const newFilter = hasAny ? { ...tempValues } : null;
                                            setAppliedFilter(newFilter);
                                            setFilterPopupOpen(false);
                                            setCurrentPage(1);
                                            fetchPage(1, searchTerm, newFilter);
                                        }}
                                        onClear={() => {
                                            const empty = { status: [], condition: [], base_price_min: '', base_price_max: '' };
                                            setTempValues(empty);
                                            setAppliedFilter(null);
                                            setFilterPopupOpen(false);
                                            setCurrentPage(1);
                                            fetchPage(1, searchTerm, null);
                                        }}
                                        enableAutoSwap={true}
                                    />

                                </div>
                            </div>

                            {/* ── Empty state ── */}
                            {showEmptyState && (
                                <div className="empty-state">
                                    <div className="empty-state-content">
                                        <div className="icon">
                                            <img src={emptyImage} alt="empty" style={{ width: 120, height: 120 }} />
                                        </div>
                                        <h1 className="no-item-title mt-2">{emptyTitle}</h1>
                                        <p className="no-item-text">{emptyMessage}</p>
                                    </div>
                                </div>
                            )}

                            {/* ── Table ── */}
                            {!showEmptyState && <div className="table-wrapper" style={{ border: 'none', overflowX: 'auto', position: 'relative' }}>
                                <table style={{
                                    width: '100%', borderCollapse: 'collapse',
                                }}>

                                    {/* ══ HEADER ══ */}
                                    <thead>
                                        <tr>
                                            <th style={{ ...thS, textAlign: 'left', minWidth: 170, position: 'sticky', left: 0, zIndex: 4, background: 'var(--Gray-200, #E9ECEF)' }}>Item</th>
                                            <th style={{ ...thS, textAlign: 'left', minWidth: 90, position: 'sticky', left: 170, zIndex: 4, background: 'var(--Gray-200, #E9ECEF)' }}>Condition</th>
                                            <th style={{ ...thS,textAlign: 'left', minWidth: 110 }}>Base Price</th>
                                            <th colSpan={2} style={{ ...thS,textAlign: 'left', minWidth: 120 }}>Activated</th>
                                            {/* Receipt: value | label | whatsapp */}
                                            <th colSpan={2} style={{ ...thS ,textAlign: 'left'}}>Receipt</th>
                                            {/* Set Type: type-label | value */}
                                            <th colSpan={2} style={{ ...thS ,textAlign: 'left'}}>Set Type</th>
                                            {/* Screen Condition: label | value */}
                                            <th colSpan={2} style={{ ...thS ,textAlign: 'left'}}>Screen Condition</th>
                                            {/* Exterior Condition: label | value */}
                                            <th colSpan={2} style={{ ...thS ,textAlign: 'left'}}>Exterior Condition</th>
                                            {/* Battery Health: label | value */}
                                            <th colSpan={2} style={{ ...thS ,textAlign: 'left'}}>Battery Health</th>
                                            {/* Original Accessories: label | value */}
                                            <th colSpan={2} style={{ ...thS ,textAlign: 'left'}}>Original Accessories</th>
                                            {/* Other Issues: label1 | val1 | label2 | val2 */}
                                            <th colSpan={4} style={{ ...thS, textAlign: 'left', width: '100%' }}>Other Issues</th>
                                            <th style={{ ...thS, minWidth: 60, position: 'sticky', right: 0, zIndex: 4, background: 'var(--Gray-200, #E9ECEF)', boxShadow: '-4px 0 6px -4px rgba(0,0,0,0.15)' }}>Action</th>
                                        </tr>
                                    </thead>

                                    {/* ══ BODY ══ */}
                                    <tbody>
                                        {pageData.map((item, itemIdx) =>
                                                item.tiers.map((tier, tIdx) => {
                                                    const bg = itemIdx % 2 === 0 ? '#FFFFFF' : '#F8F9FA';
                                                    const isLastTier = tIdx === TIERS_PER_ITEM - 1;
                                                    const rowBorder = isLastTier
                                                        ? { borderBottom: '1px solid var(--Gray-300, #DEE2E6)' }
                                                        : {};
                                                    return (
                                                        <tr key={`${item.id}-${tIdx}`} style={{ background: bg, cursor: 'pointer' }} onClick={() => { setSelectedItem(item); setDrawerOpen(true); }}>

                                                            {/* ── rowspan cells (first tier only) ── */}
                                                            {tIdx === 0 && (
                                                                <>
                                                                    {/* Item */}
                                                                    <td rowSpan={TIERS_PER_ITEM} style={{
                                                                        ...tdL, verticalAlign: 'center',
                                                                        background: bg,
                                                                        borderBottom: '1px solid var(--Gray-300, #DEE2E6)',
                                                                        position: 'sticky', left: 0,
                                                                        zIndex: hoveredPromo === item.id ? 9999 : 1,
                                                                        overflow: 'visible',
                                                                    }}>
                                                                        <div style={{ display: 'flex', alignItems: 'flex-center', gap: '10px' }}>
                                                                            {/* Col 1: Image centered */}
                                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                                                <img
                                                                                    src={item.tradein_image_url || deviceDefaultImage} alt={item.sku_name}
                                                                                    style={{ width: 32, height: 32, objectFit: 'cover' }}
                                                                                    onError={e => { e.target.onerror = null; e.target.src = noImage; }}
                                                                                />
                                                                            </div>
                                                                            {/* Col 2: Text details */}
                                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                                                <span style={{ fontWeight: 400, fontSize: '12px', fontFamily: 'Montserrat', color: '#212529' }}>
                                                                                    {item.storage}
                                                                                </span>
                                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                                    <span style={{
                                                                                        width: 12, height: 12, borderRadius: '50%',
                                                                                        background: item.colorCode,
                                                                                        border: '1.5px solid #fff',
                                                                                        boxShadow: '0 0 0 1px #000',
                                                                                        flexShrink: 0, display: 'inline-block',
                                                                                    }} />
                                                                                    <span style={{ fontSize: '10px', color: '#6C757D', fontFamily: 'Montserrat' }}>
                                                                                        {item.color}
                                                                                    </span>
                                                                                </div>
                                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px',padding:'2px 5px' }}>
                                                                                    <TypeBadge isPromotion={item.isPromotion} />
                                                                                    {item.isPromotion && (
                                                                                        <span
                                                                                            style={{ position: 'relative', display: 'inline-flex', cursor: 'pointer', zIndex: hoveredPromo === item.id ? 99999 : 'auto', isolation: 'isolate' }}
                                                                                            onMouseEnter={() => setHoveredPromo(item.id)}
                                                                                            onMouseLeave={() => setHoveredPromo(null)}
                                                                                        >
                                                                                            <PromoIcon />
                                                                                            {hoveredPromo === item.id && <PromoPopup promo={item.promo} />}
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </td>

                                                                    {/* Condition */}
                                                                    <td rowSpan={TIERS_PER_ITEM} style={{
                                                                        ...tdC, background: bg, verticalAlign: 'middle',
                                                                        borderBottom: '1px solid var(--Gray-300, #DEE2E6)',
                                                                        position: 'sticky', left: 170, zIndex: 1,
                                                                    }}>
                                                                        <ConditionBadge condition={item.itemCondition} />
                                                                    </td>

                                                                    {/* Base Price */}
                                                                    <td rowSpan={TIERS_PER_ITEM} style={{
                                                                        ...tdC, background: bg, verticalAlign: 'middle',
                                                                        borderBottom: '1px solid var(--Gray-300, #DEE2E6)',
                                                                    }}>
                                                                        <span style={{
                                                                            fontFamily: 'Montserrat', fontSize: '12px', fontStyle: 'normal',
                                                                            fontWeight: 400, lineHeight: 'normal',
                                                                            color: 'var(--Gray-900, #212529)', whiteSpace: 'nowrap',
                                                                        }}>
                                                                            {item.basePrice}
                                                                        </span>
                                                                    </td>
                                                                </>
                                                            )}

                                                            {/* ── Activated ── */}
                                                            <td style={{ ...tdL, ...rowBorder, whiteSpace: 'nowrap' }}>
                                                                <span style={{
                                                                    fontFamily: 'Montserrat', fontSize: '11px', fontStyle: 'normal',
                                                                    fontWeight: 400, lineHeight: 'normal',
                                                                    color: 'var(--Gray-600, #6C757D)',
                                                                }}>
                                                                    {tier.label}
                                                                </span>
                                                            </td>

                                                            {/* ── Receipt: value | label | whatsapp ── */}
                                                            <td style={{ ...tdC, ...rowBorder, minWidth: 50, textAlign: 'left' }}>
                                                                <Val v={tier.receiptValue} />
                                                            </td>
                                                            <td style={{ ...tdC, ...rowBorder, minWidth: 65, textAlign: 'left' }}>
                                                                <Lbl v={tier.receiptLabel} />
                                                            </td>
                                                            <td style={{ ...tdC, ...rowBorder, minWidth: 75, textAlign: 'left' }}>
                                                                <Val v={tier.receiptWhatsapp} />
                                                            </td>

                                                            {/* ── Set Type: type-label | value ── */}
                                                            <td style={{ ...tdC, ...rowBorder, minWidth: 65, textAlign: 'left' }}>
                                                                <Lbl v={tier.setTypeLabel} />
                                                            </td>
                                                            <td style={{ ...tdC, ...rowBorder, minWidth: 80, textAlign: 'left' }}>
                                                                <Val v={tier.setTypeValue} />
                                                            </td>

                                                            {/* ── Screen Condition: label | value ── */}
                                                            <td style={{ ...tdC, ...rowBorder, minWidth: 130, textAlign: 'left' }}>
                                                                <Lbl v={tier.screenLabel} />
                                                            </td>
                                                            <td style={{ ...tdC, ...rowBorder, minWidth: 50 }}>
                                                                <Val v={tier.screenValue} />
                                                            </td>

                                                            {/* ── Exterior Condition: label | value ── */}
                                                            <td style={{ ...tdC, ...rowBorder, minWidth: 130, textAlign: 'left' }}>
                                                                <Lbl v={tier.exteriorLabel} />
                                                            </td>
                                                            <td style={{ ...tdC, ...rowBorder, minWidth: 50 }}>
                                                                <Val v={tier.exteriorValue} />
                                                            </td>

                                                            {/* ── Battery Health: label | value ── */}
                                                            <td style={{ ...tdC, ...rowBorder, minWidth: 110, textAlign: 'left' }}>
                                                                <Lbl v={tier.batteryLabel} />
                                                            </td>
                                                            <td style={{ ...tdC, ...rowBorder, minWidth: 50 }}>
                                                                <Val v={tier.batteryValue} />
                                                            </td>

                                                            {/* ── Original Accessories: label | value ── */}
                                                            <td style={{ ...tdC, ...rowBorder, minWidth: 120, textAlign: 'left' }}>
                                                                <Lbl v={tier.accessoryLabel} />
                                                            </td>
                                                            <td style={{ ...tdC, ...rowBorder, minWidth: 50 }}>
                                                                <Val v={tier.accessoryValue} />
                                                            </td>

                                                            {/* ── Other Issues: label1 | val1 | label2 | val2 ── */}
                                                            <td style={{ ...tdC, ...rowBorder, minWidth: 145, textAlign: 'left' }}>
                                                                <Lbl v={tier.issue1Label} />
                                                            </td>
                                                            <td style={{ ...tdC, ...rowBorder, minWidth: 50 }}>
                                                                <Val v={tier.issue1Value} />
                                                            </td>
                                                            <td style={{ ...tdC, ...rowBorder, minWidth: 145, textAlign: 'left' }}>
                                                                <Lbl v={tier.issue2Label} />
                                                            </td>
                                                            <td style={{ ...tdC, ...rowBorder, minWidth: 50 }}>
                                                                <Val v={tier.issue2Value} />
                                                            </td>

                                                            {/* ── Action (first tier only, rowspan) ── */}
                                                            {tIdx === 0 && (
                                                                <td rowSpan={TIERS_PER_ITEM} style={{
                                                                    ...tdC, background: bg, verticalAlign: 'middle',
                                                                    borderBottom: '1px solid var(--Gray-300, #DEE2E6)',
                                                                    position: 'sticky', right: 0, zIndex: 1,
                                                                    boxShadow: '-4px 0 6px -4px rgba(0,0,0,0.15)',
                                                                }}>
                                                                    <button
                                                                        ref={(el) => (buttonRefs.current[item.id] = el)}
                                                                        onClick={(e) => toggleDropdown(item.id, e)}
                                                                        style={{
                                                                            border:       'none',
                                                                            cursor:       'pointer',
                                                                            display:      'inline-flex',
                                                                            alignItems:   'center',
                                                                            padding:      4,
                                                                            transition:   'background 0.15s ease, box-shadow 0.15s ease',
                                                                            borderRadius: dropdownOpenId === item.id ? '8px' : '',
                                                                            background:   dropdownOpenId === item.id ? '#FFF' : 'transparent',
                                                                            boxShadow:    dropdownOpenId === item.id ? '0 1px 4px 0 rgba(0,0,0,0.10)' : 'none',
                                                                        }}
                                                                    >
                                                                        <MoreHorizontal size={20} />
                                                                    </button>
                                                                </td>
                                                            )}
                                                        </tr>
                                                    );
                                                })
                                            )}
                                    </tbody>
                                </table>

                                {/* Action dropdown */}
                                {dropdownOpenId !== null && (
                                    <div
                                        ref={dropdownRef}
                                        className="backdrop-cls"
                                        style={{
                                            position: 'fixed',
                                            top: dropdownPosition.top,
                                            right: dropdownPosition.right,
                                            background: 'white',
                                            boxShadow: '0px 1px 4px 0px #0000001A',
                                            zIndex: 1000, minWidth: 160, borderRadius: 8,
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <div className="btn-align-forall">
                                            <div className="common-for-all">
                                                <button onClick={() => {
                                                    const item = pageData.find(i => i.id === dropdownOpenId);
                                                    setSelectedItem(item);
                                                    setDrawerOpen(true);
                                                    setDropdownOpenId(null);
                                                }}>View Details</button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>}

                            {/* Footer */}
                            <div style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'end',
                                marginTop: '12px', flexWrap: 'wrap', gap: '8px',
                            }}>
                                <span style={{ fontFamily: 'Montserrat', fontSize: '12px', color: '#6C757D' }}>
                                    {totalCount > 0 ? `${startCount} - ${endCount} of ${totalCount}` : '0 items'}
                                </span>
                                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={(p) => { setCurrentPage(p); fetchPage(p, searchTerm, appliedFilter); }} />
                            </div>

                        </div>
                    </div>
                </div>
            </div>
            {/* ── Item Info Drawer ── */}
            {drawerOpen && (
                <TradeInItemDrawer
                    item={selectedItem}
                    deviceDefaultImage={deviceDefaultImage}
                    onClose={() => { setDrawerOpen(false); setSelectedItem(null); }}
                />
            )}

             {/* CSS — matching PlatformOrderPage */}
        <style jsx>{`
            .search-box {
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 8px 12px;
                border: 1px solid #DEE2E6;
                border-radius: 8px;
                background: white;
            }
            .search-box input {
                border: none;
                outline: none;
                width: 100%;
                font-size: 14px;
                background: transparent;
            }
        `}</style>
        </div>

       
    );
}
